import { Link } from "react-router-dom";
import { Mail, Phone, MapPin, Linkedin, Instagram, ArrowUpRight, Lock, ShieldCheck, Award, Database } from "lucide-react";
import Logo from "@/components/Logo";

const sections = [
  {
    title: "Nos secteurs",
    links: [
      { to: "/industrie", label: "Industrie" },
      { to: "/tertiaire", label: "Tertiaire & Commerce" },
      { to: "/residentiel", label: "Résidentiel & Copropriétés" },
    ],
  },
  {
    title: "À propos",
    links: [
      { to: "/financement-cee", label: "Financement CEE" },
      { to: "/certifications", label: "Nos certifications" },
      { to: "/realisations", label: "Réalisations" },
      { to: "/a-propos", label: "L'entreprise" },
      { to: "/contact", label: "Nous contacter" },
    ],
  },
  {
    title: "Solutions",
    links: [
      { to: "/solutions", label: "Toutes les expertises" },
      { to: "/eligibilite", label: "Vérifier mon éligibilité" },
      { to: "/industrie", label: "Audit industriel" },
      { to: "/tertiaire", label: "Audit tertiaire" },
    ],
  },
];

export default function Footer() {
  return (
    <footer data-testid="site-footer" className="relative bg-[#0A0F1A] text-white overflow-hidden">
      <div className="absolute inset-0 bg-grid-soft opacity-[0.06] pointer-events-none" />
      <div className="absolute -top-32 -right-32 h-[480px] w-[480px] rounded-full bg-[#10B981]/15 blur-3xl" />
      <div className="absolute -bottom-32 -left-32 h-[420px] w-[420px] rounded-full bg-[#0055A4]/10 blur-3xl" />

      <div className="relative max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-10 pt-24 pb-12">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 mb-16">
          <div className="lg:col-span-5">
            <div className="mb-6">
              <Logo variant="light" showTagline />
            </div>
            <p className="text-white/65 leading-relaxed max-w-md mb-8">
              ECONATION est le partenaire de référence des industriels, gestionnaires de patrimoine
              et particuliers qui financent leur transition énergétique grâce aux Certificats
              d&apos;Économies d&apos;Énergie.
            </p>
            <div className="flex flex-wrap gap-3">
              <a href="https://linkedin.com" target="_blank" rel="noreferrer" data-testid="footer-social-linkedin" className="h-10 w-10 rounded-full bg-white/5 hover:bg-white/10 border border-white/10 grid place-items-center transition-colors">
                <Linkedin className="h-4 w-4" />
              </a>
              <a href="https://instagram.com" target="_blank" rel="noreferrer" data-testid="footer-social-instagram" className="h-10 w-10 rounded-full bg-white/5 hover:bg-white/10 border border-white/10 grid place-items-center transition-colors">
                <Instagram className="h-4 w-4" />
              </a>
            </div>
          </div>

          {sections.map((s) => (
            <div key={s.title} className="lg:col-span-2">
              <div className="text-[11px] uppercase tracking-[0.24em] text-[#10B981] font-semibold mb-5">{s.title}</div>
              <ul className="space-y-3">
                {s.links.map((l) => (
                  <li key={l.to}>
                    <Link to={l.to} className="text-sm text-white/75 hover:text-white inline-flex items-center gap-1.5 group">
                      {l.label}
                      <ArrowUpRight className="h-3 w-3 opacity-0 -translate-x-1 group-hover:opacity-100 group-hover:translate-x-0 transition-all" />
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          ))}

          <div className="lg:col-span-3">
            <div className="text-[11px] uppercase tracking-[0.24em] text-[#10B981] font-semibold mb-5">Contact</div>
            <ul className="space-y-4 text-sm text-white/75">
              <li className="flex items-start gap-3"><Mail className="h-4 w-4 mt-0.5 text-[#10B981] shrink-0" /><a href="mailto:contact@econation.fr" className="hover:text-white">contact@econation.fr</a></li>
              <li className="flex items-start gap-3"><Phone className="h-4 w-4 mt-0.5 text-[#10B981] shrink-0" /><a href="tel:+33186901448" className="hover:text-white">01 86 90 14 48</a></li>
              <li className="flex items-start gap-3"><MapPin className="h-4 w-4 mt-0.5 text-[#10B981] shrink-0" /><span>Paris · Lyon · Marseille · Toulouse · Lille</span></li>
            </ul>
          </div>
        </div>

        <div className="pt-8 border-t border-white/10">
          {/* Bandeau sécurité institutionnelle */}
          <div data-testid="security-band" className="mb-6 grid grid-cols-2 sm:grid-cols-4 gap-3">
            {[
              { icon: Lock, label: "Connexion sécurisée SSL/HTTPS", sub: "Chiffrement TLS 1.3" },
              { icon: ShieldCheck, label: "Conforme RGPD", sub: "Données protégées" },
              { icon: Award, label: "Mandataire MPR · Anah · CEE", sub: "Enregistrement officiel" },
              { icon: Database, label: "Hébergement France", sub: "Serveurs sécurisés EU" },
            ].map((item) => (
              <div
                key={item.label}
                className="flex items-start gap-2.5 rounded-xl border border-white/10 bg-white/[0.03] px-3.5 py-2.5"
              >
                <div className="h-8 w-8 rounded-lg bg-[#10B981]/15 text-[#10B981] grid place-items-center flex-shrink-0">
                  <item.icon className="h-3.5 w-3.5" />
                </div>
                <div className="min-w-0">
                  <div className="text-white text-[11.5px] font-semibold leading-tight">{item.label}</div>
                  <div className="text-white/45 text-[10px] mt-0.5">{item.sub}</div>
                </div>
              </div>
            ))}
          </div>
          <div className="pt-4 border-t border-white/5 flex flex-col md:flex-row items-start md:items-center justify-between gap-4 text-xs text-white/45">
          <div>© {new Date().getFullYear()} ECONATION. Tous droits réservés. <span className="text-white/30">· Musique : &quot;Prelude and Action&quot; par Kevin MacLeod (incompetech.com) — CC BY 4.0</span></div>
          <div className="flex flex-wrap gap-x-6 gap-y-2">
            <Link to="/mentions-legales" className="hover:text-white/80">Mentions légales</Link>
            <Link to="/confidentialite" className="hover:text-white/80">Politique de confidentialité</Link>
            <Link to="/cookies" className="hover:text-white/80">Cookies</Link>
            <span>Délégataire CEE certifié · ISO 50001</span>
          </div>
          </div>
        </div>
      </div>
    </footer>
  );
}
